import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Shield, CheckCircle2, LogOut } from "lucide-react";

/**
 * Blocking page the user lands on right after first login when the admin
 * created their account. Forces them to change the initial password before
 * doing anything else.
 *
 * Also reachable voluntarily by any authenticated user to rotate their password.
 */
export default function ChangePassword() {
  const t = useT();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const forced = !!user?.must_change_password;

  const strength = (() => {
    if (!next) return { label: "", pct: 0, color: "#6B7280" };
    const hasLower = /[a-z]/.test(next);
    const hasUpper = /[A-Z]/.test(next);
    const hasNum = /\d/.test(next);
    const hasSym = /[^A-Za-z0-9]/.test(next);
    const longOk = next.length >= 10;
    const score = [hasLower, hasUpper, hasNum, hasSym, longOk].filter(Boolean).length;
    if (score <= 2) return { label: t("pwd.strength.weak"), pct: 30, color: "#DC2626" };
    if (score <= 4) return { label: t("pwd.strength.ok"), pct: 65, color: "#F4B81F" };
    return { label: t("pwd.strength.strong"), pct: 100, color: "#1B2E7A" };
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error(t("pwd.err.too_short"));
      return;
    }
    if (next === current) {
      toast.error(t("pwd.err.same_as_current"));
      return;
    }
    if (next !== confirm) {
      toast.error(t("pwd.err.mismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(t("pwd.ok"));
      if (refreshUser) await refreshUser();
      // Send user to their dashboard
      if (user?.role === "admin") navigate("/admin");
      else navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("pwd.err.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-6" data-testid="change-password-page">
      <div className="w-full max-w-md">
        {forced && (
          <div className="bg-[#F4B81F]/15 border border-[#F4B81F]/40 rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="must-change-banner">
            <Shield className="h-4 w-4 text-[#F4B81F] mt-0.5 shrink-0" strokeWidth={1.8} />
            <div>
              <p className="text-xs font-medium text-[#1A1A1A]">{t("pwd.forced.title")}</p>
              <p className="text-[11px] text-[#6B7280] leading-snug mt-0.5">
                {t("pwd.forced.subtitle")}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="h-5 w-5 text-[#1B2E7A]" strokeWidth={1.6} />
            <h1 className="font-heading text-2xl text-[#1A1A1A]">{t("pwd.title")}</h1>
          </div>
          <p className="text-xs text-[#6B7280] mb-6">
            {user?.email} · {user?.role === "admin" ? t("user.role.admin") : t("user.role.salesperson")}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
            <div>
              <label className="block text-xs text-[#374151] mb-1" htmlFor="pwd-current">
                {t("pwd.current")}
              </label>
              <Input
                id="pwd-current"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                data-testid="pwd-current"
                placeholder={forced ? t("pwd.current.placeholder_forced") : ""}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-[#374151] mb-1" htmlFor="pwd-new">
                {t("pwd.new")}
              </label>
              <Input
                id="pwd-new"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                data-testid="pwd-new"
                required
              />
              {next && (
                <div className="mt-1.5">
                  <div className="h-1 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{ width: `${strength.pct}%`, backgroundColor: strength.color }}
                    />
                  </div>
                  <p className="text-[10px] mt-1" style={{ color: strength.color }} data-testid="pwd-strength">
                    {strength.label}
                  </p>
                </div>
              )}
              <p className="text-[10px] text-[#6B7280] mt-1">{t("pwd.new.hint")}</p>
            </div>
            <div>
              <label className="block text-xs text-[#374151] mb-1" htmlFor="pwd-confirm">
                {t("pwd.confirm")}
              </label>
              <Input
                id="pwd-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="pwd-confirm"
                required
              />
              {confirm && confirm === next && (
                <p className="text-[10px] text-[#1B2E7A] mt-1 inline-flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> {t("pwd.match_ok")}
                </p>
              )}
            </div>
            <div className="pt-2 flex items-center justify-between gap-3">
              {!forced && (
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="text-xs text-[#6B7280] hover:text-[#374151]"
                  data-testid="pwd-cancel"
                >
                  {t("pwd.cancel")}
                </button>
              )}
              {forced && (
                <button
                  type="button"
                  onClick={logout}
                  className="text-xs text-[#6B7280] hover:text-[#DC2626] inline-flex items-center gap-1"
                  data-testid="pwd-logout"
                >
                  <LogOut className="h-3 w-3" />
                  {t("user.logout")}
                </button>
              )}
              <Button
                type="submit"
                disabled={busy || !current || !next || !confirm}
                className="bg-[#1B2E7A] hover:bg-[#13205A] text-white ml-auto"
                data-testid="pwd-submit"
              >
                {busy ? t("pwd.saving") : t("pwd.save")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
